import ApiService from '../apiService'
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  MfaVerificationRequest,
  ValidateTokenResponse,
  StartPhoneVerificationRequest,
  StartPhoneVerificationResponse,
  VerifyPhoneRequest,
  VerifyPhoneResponse,
  CompleteRegistrationRequest,
  CompleteRegistrationResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  VerifyResetCodeRequest,
  VerifyResetCodeResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  OAuthProvidersResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  SessionDto,
  UpdateSessionNameRequest,
  TokenDurationSettingRequest,
} from '../types/auth'

export const authApi = {
  // Login
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await ApiService.post<LoginResponse>(
      '/api/auth/login',
      credentials,
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Register (Legacy - Obsolete)
  register: (data: RegisterRequest) =>
    ApiService.post<{ message: string }>('/api/auth/register', data),

  // Phone Verification Registration Flow
  // Step 1: Start phone verification
  startPhoneVerification: async (data: StartPhoneVerificationRequest): Promise<StartPhoneVerificationResponse> => {
    try {
      const response = await ApiService.post<StartPhoneVerificationResponse>(
        '/api/auth/register/start-phone-verification',
        data,
        {
          headers: {
            Accept: '*/*',
          },
        }
      )
      return response
    } catch (err: any) {
      // If the error response contains the data we need, return it
      if (err.response?.data) {
        return err.response.data
      }
      throw err
    }
  },

  // Step 2: Verify phone code
  verifyPhone: async (data: VerifyPhoneRequest): Promise<VerifyPhoneResponse> => {
    try {
      const response = await ApiService.post<VerifyPhoneResponse>(
        '/api/auth/register/verify-phone',
        data,
        {
          headers: {
            Accept: '*/*',
          },
        }
      )
      return response
    } catch (err: any) {
      // If the error response contains the data we need, return it
      if (err.response?.data) {
        return err.response.data
      }
      throw err
    }
  },

  // Step 3: Complete registration
  completeRegistration: async (data: CompleteRegistrationRequest): Promise<CompleteRegistrationResponse> => {
    try {
      const response = await ApiService.post<CompleteRegistrationResponse>(
        '/api/auth/register/complete',
        data,
        {
          headers: {
            Accept: '*/*',
          },
        }
      )
      return response
    } catch (err: any) {
      // If the error response contains the data we need, return it
      if (err.response?.data) {
        return err.response.data
      }
      throw err
    }
  },

  // Verify MFA
  verifyMfa: async (data: MfaVerificationRequest): Promise<LoginResponse> => {
    const response = await ApiService.post<LoginResponse>(
      '/api/auth/verify-mfa',
      data,
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Validate Token
  validateToken: async (): Promise<ValidateTokenResponse> => {
    const response = await ApiService.get<ValidateTokenResponse>(
      '/api/auth/validate-token',
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Refresh Token
  refreshToken: async (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    const response = await ApiService.post<RefreshTokenResponse>(
      '/api/auth/refresh',
      data,
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Session Management
  getSessions: async (): Promise<SessionDto[]> => {
    const response = await ApiService.get<SessionDto[]>(
      '/api/me/sessions',
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  updateSessionName: async (sessionId: string, data: UpdateSessionNameRequest): Promise<void> => {
    await ApiService.patch<void>(
      `/api/me/sessions/${sessionId}/name`,
      data
    )
  },

  revokeSession: async (sessionId: string): Promise<void> => {
    await ApiService.delete<void>(`/api/me/sessions/${sessionId}`)
  },

  // Token Duration Setting
  updateTokenDuration: async (data: TokenDurationSettingRequest): Promise<void> => {
    await ApiService.patch<void>(
      '/api/me/settings/token-duration',
      data
    )
  },

  // Logout
  logout: async (): Promise<{ message: string }> => {
    const response = await ApiService.post<{ message: string }>(
      '/api/auth/logout',
      {},
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Change Password
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await ApiService.post<{ message: string }>(
      '/api/auth/change-password',
      {
        currentPassword,
        newPassword,
      },
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Password Reset Flow
  // Step 1: Request password reset
  forgotPassword: async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    // Use */* Accept header to avoid 406 errors from ASP.NET Core
    const response = await ApiService.post<ForgotPasswordResponse>(
      '/api/auth/forgot-password',
      data,
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Step 2: Verify SMS code (SMS flow only)
  verifyResetCode: async (data: VerifyResetCodeRequest): Promise<VerifyResetCodeResponse> => {
    const response = await ApiService.post<VerifyResetCodeResponse>(
      '/api/auth/verify-reset-code',
      data,
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // Step 3: Reset password with token
  resetPassword: async (data: ResetPasswordRequest): Promise<ResetPasswordResponse> => {
    const response = await ApiService.post<ResetPasswordResponse>(
      '/api/auth/reset-password',
      data,
      {
        headers: {
          Accept: '*/*',
        },
      }
    )
    return response
  },

  // External Login (OAuth)
  initiateExternalLogin: (provider: string, returnUrl: string) => {
    // This will redirect to the OAuth provider via our proxy
    window.location.href = `/api/auth/external-login?provider=${encodeURIComponent(provider)}&returnUrl=${encodeURIComponent(returnUrl)}`
  },

  // Get available OAuth providers
  getOAuthProviders: async () => {
    const response = await ApiService.get<OAuthProvidersResponse>('/api/auth/oauth/providers')
    return response
  },
}
